import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Search, User, DollarSign, Ban, Stethoscope } from 'lucide-react';
import FamilyView from './FamilyView';

export default function PatientSearchSection({
  searchQuery, setSearchQuery, onSearch, loading,
  selectedPatient, searchType, familyInfo, familyMembers, familyClaimHistory,
  role, isSuperAdmin, workflowEnabled, preauthRequired = false, hospitalCurrency, formatCurrency,
  toLocalBalance, toLocalAmount,
  patientBypassStates, onToggleBypass, onCheckIn, onCreateClaimForMember, onViewClaimDetails,
}) {
  return (
    <Card className="border-blue-200 shadow-md">
      <CardHeader className="bg-gradient-to-r from-blue-50 to-blue-100 border-b border-blue-200 p-3 sm:p-4">
        <CardTitle className="flex items-center gap-2 text-blue-900 text-sm sm:text-base">
          <Search className="w-4 h-4 sm:w-5 sm:h-5" />
          Patient Search
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3 sm:pt-6">
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
          <div className="flex-1">
            <Input
              data-testid="patient-search-input"
              placeholder="Search by Serial Number or Name"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && onSearch()}
              className="h-10 sm:h-11 border-gray-300 text-sm sm:text-base"
            />
          </div>
          <Button
            data-testid="patient-search-button"
            onClick={onSearch}
            disabled={loading}
            className="h-10 sm:h-11 bg-blue-600 hover:bg-blue-700 px-4 sm:px-8"
          >
            <Search className="w-4 h-4 mr-2" />
            Search
          </Button>
        </div>

        {selectedPatient && searchType === 'individual' && (
          <div className="mt-4 sm:mt-6 p-3 sm:p-6 bg-gradient-to-br from-blue-50 to-white rounded-xl border border-blue-200" data-testid="patient-details">
            {selectedPatient.is_suspended && (
              <div className="mb-4 p-4 bg-red-100 border border-red-300 rounded-lg">
                <div className="flex items-start gap-3">
                  <Ban className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-bold text-red-800">Account Suspended</h4>
                    <p className="text-sm text-red-700 mt-1">
                      {selectedPatient.suspended_message || "This member is suspended. Please contact System Administrator or Acacia Insurance office."}
                    </p>
                  </div>
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <div className="space-y-2 sm:space-y-3">
                <div className="flex items-center gap-2 text-gray-700 text-sm sm:text-base">
                  <User className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  <span className="font-semibold">Patient:</span>
                  <span data-testid="patient-name" className="truncate">{selectedPatient.first_name} {selectedPatient.middle_name} {selectedPatient.last_name}</span>
                  {selectedPatient.is_suspended && (
                    <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full font-medium">SUSPENDED</span>
                  )}
                </div>
                <div className="text-xs sm:text-sm text-gray-600">
                  <span className="font-medium">Serial:</span> <span data-testid="patient-serial">{selectedPatient.serial_number}</span>
                </div>
                <div className="text-xs sm:text-sm text-gray-600">
                  <span className="font-medium">Family ID:</span> {selectedPatient.family_id}
                </div>
                <div className="text-xs sm:text-sm text-gray-600">
                  <span className="font-medium">DOB:</span> {selectedPatient.dob} • {selectedPatient.sex}
                </div>
              </div>
              <div className="flex items-center justify-center md:justify-end">
                <div className={`${selectedPatient.is_suspended ? 'bg-gradient-to-br from-gray-400 to-gray-500' : 'bg-gradient-to-br from-green-500 to-green-600'} text-white px-4 sm:px-8 py-4 sm:py-6 rounded-2xl shadow-lg text-center`}>
                  <DollarSign className="w-6 h-6 sm:w-8 sm:h-8 mx-auto mb-1 sm:mb-2" />
                  <div className="text-xs sm:text-sm font-medium opacity-90">Available Balance</div>
                  <div className="text-3xl font-bold" data-testid="patient-balance">{formatCurrency(toLocalBalance(selectedPatient.remaining_balance))}</div>
                  {!isSuperAdmin && hospitalCurrency.code !== 'USD' && (
                    <div className="text-xs opacity-90 mt-1">
                      (≈ ${selectedPatient.remaining_balance.toFixed(2)} USD)
                    </div>
                  )}
                </div>
              </div>
            </div>
            {!selectedPatient.is_suspended && !isSuperAdmin && workflowEnabled && (role === 'Finance' || role === 'Admin' || role === 'Reception') && (
              <div className="mt-4 flex justify-end">
                <Button
                  onClick={() => onCheckIn(selectedPatient)}
                  className="bg-teal-600 hover:bg-teal-700"
                  data-testid="checkin-selected-patient"
                >
                  <Stethoscope className="w-4 h-4 mr-2" />
                  Check In for Consultation
                </Button>
              </div>
            )}
          </div>
        )}

        {familyInfo && searchType === 'family' && (
          <FamilyView
            familyInfo={familyInfo}
            familyMembers={familyMembers}
            familyClaimHistory={familyClaimHistory}
            role={role}
            isSuperAdmin={isSuperAdmin}
            workflowEnabled={workflowEnabled}
            preauthRequired={preauthRequired}
            hospitalCurrency={hospitalCurrency}
            formatCurrency={formatCurrency}
            toLocalBalance={toLocalBalance}
            toLocalAmount={toLocalAmount}
            patientBypassStates={patientBypassStates}
            onToggleBypass={onToggleBypass}
            onCheckIn={onCheckIn}
            onCreateClaimForMember={onCreateClaimForMember}
            onViewClaimDetails={onViewClaimDetails}
          />
        )}
      </CardContent>
    </Card>
  );
}
